import React from 'react';
import { useTheme } from '@emotion/react';
import styled from '@emotion/styled';
import {
  RotateCcw,
  SkipBack,
  Play,
  Pause,
  SkipForward,
  RotateCw,
  Square,
} from 'react-feather';
import AudioPlayerTrackProgress from '@/components/AudioPlayer/Progress.Track';

interface AudioControlsProps {
  isPlaying: boolean;
  onTogglePlay: () => void;
  onPreviousTrack: () => void;
  onNextTrack: () => void;
  onSkipBackward: () => void;
  onSkipAhead: () => void;
  onStop: () => void;
  currentTime?: number;
  duration?: number;
  onScan: (time: number) => void;
  onSeek?: (time: number) => void;
  disabledPreviousTrack?: boolean;
  disabledNextTrack?: boolean;
}

const ControlsContainer = styled('div')({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 2,
});

const Button = styled('button')(({ theme }) => ({
  background: 'none',
  border: 'none',
  width: 26,
  height: 26,
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: '50%',
  '&:hover': {
    backgroundColor: theme.palette.gray.light5,
  },
  '&:disabled': {
    svg: {
      fill: theme.palette.gray.light1,
      stroke: theme.palette.gray.light1,
    },
  },
  '&:disabled:hover': {
    cursor: 'default',
    background: 'none',
  },
  transition: 'background-color 110ms ease-in-out',
}));

const RotateButton = styled('button')(({ theme }) => ({
  background: 'none',
  border: 'none',
  width: 26,
  height: 26,
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: '50%',
  '&:hover': {
    backgroundColor: theme.palette.gray.light5,
  },
  '&:disabled': {
    svg: {
      stroke: theme.palette.gray.light1,
    },
  },
  '&:disabled:hover': {
    background: 'none',
    cursor: 'default',
    svg: {
      stroke: theme.palette.gray.light1,
    },
  },
  transition: 'background-color 110ms ease-in-out',
}));

const Filled = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  svg: {
    fill: theme.palette.gray.dark1,
    stroke: theme.palette.gray.dark1,
  },
}));

const TimeDisplay = styled('span')(({ theme }) => ({
  fontSize: '0.8rem',
  minWidth: '50px',
  whiteSpace: 'nowrap',
  color: theme.palette.gray.dark1,
}));

const ProgressAndTimer = styled('div')({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  marginTop: '8px',
});

const StyledProgressTrack = styled(AudioPlayerTrackProgress)({
  width: 100,
});

function formatTime(seconds?: number) {
  if (!seconds || isNaN(seconds)) return '0:00';
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s < 10 ? '0' + s : s}`;
}

// TODO: implement disabled state (styling) for buttons

const AudioControls: React.FC<AudioControlsProps> = ({
  isPlaying,
  onTogglePlay,
  onPreviousTrack,
  onNextTrack,
  onSkipBackward,
  onSkipAhead,
  onStop,
  currentTime,
  duration,
  onScan,
  onSeek,
  disabledPreviousTrack,
  disabledNextTrack,
}) => {
  const theme = useTheme();

  return (
    <div>
      <ControlsContainer>
        <RotateButton onClick={onSkipBackward} disabled={currentTime === 0}>
          <RotateCcw size={14} />
        </RotateButton>

        <Button onClick={onPreviousTrack} disabled={disabledPreviousTrack}>
          <Filled>
            <SkipBack size={14} />
          </Filled>
        </Button>

        <Button
          style={{ width: 34, height: 34 }}
          onClick={onTogglePlay}>
          <Filled>
            {isPlaying ? <Pause size={16} /> : <Play size={16} />}
          </Filled>
        </Button>

        <Button onClick={onNextTrack} disabled={disabledNextTrack}>
          <Filled>
            <SkipForward size={14} />
          </Filled>
        </Button>

        <RotateButton onClick={onSkipAhead} disabled={disabledNextTrack}>
          <RotateCw size={14} />
        </RotateButton>

        <Button onClick={onStop}>
          <Filled>
            <Square size={14} />
          </Filled>
        </Button>
      </ControlsContainer>
      <ProgressAndTimer>
        <StyledProgressTrack
          position={currentTime}
          scan={onScan}
          seek={onSeek}
        />
        <TimeDisplay>
          {formatTime(currentTime)} / {formatTime(duration)}
        </TimeDisplay>
      </ProgressAndTimer>
    </div>
  );
};

export default AudioControls;
